import { Controller } from "@hotwired/stimulus"

// Syncs database IDs back to escalation context form items after autosave
// This prevents duplicate records when autosave fires multiple times for new items
export default class extends Controller {
  static targets = ["item"]

  connect() {
    const form = this.element.closest('form[data-controller~="autosave"]')
    if (form) {
      this.form = form
      this.interceptSave()
    }
  }

  disconnect() {
    // Restore original save if we modified it
    if (this.autosaveController && this.originalSave) {
      this.autosaveController.save = this.originalSave
    }
  }

  // Intercept autosave save to block new record fields during save
  interceptSave() {
    const autosaveController = this.application.getControllerForElementAndIdentifier(this.form, 'autosave')
    if (!autosaveController) return

    this.autosaveController = autosaveController
    this.originalSave = autosaveController.save.bind(autosaveController)

    const self = this
    autosaveController.save = function() {
      // Get new record items and lock them during save
      const newRecordItems = self.getNewRecordItems()
      self.lockItems(newRecordItems)

      const form = this.element
      const formData = new FormData(form)

      fetch(form.action, {
        method: 'PATCH',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        }
      })
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok')
        return response.json()
      })
      .then(data => {
        const status = this.statusElement
        if (status) {
          status.textContent = "Saved"
          status.classList.remove("text-gray-500", "text-red-600")
          status.classList.add("text-green-600")
          setTimeout(() => { status.textContent = "" }, 2000)
        }

        const unpublishedBanner = document.getElementById('unpublished-banner')
        if (unpublishedBanner) {
          unpublishedBanner.classList.remove('hidden')
        }

        // Sync IDs from response, then unlock
        if (data?.escalation_contexts) {
          self.syncItemIds(data.escalation_contexts)
        }
        self.unlockItems(newRecordItems)

        this.dispatch("saved", { detail: data })
      })
      .catch(error => {
        console.error('Save failed:', error)
        const status = this.statusElement
        if (status) {
          status.textContent = "Save failed"
          status.classList.remove("text-gray-500", "text-green-600")
          status.classList.add("text-red-600")
        }
        // Unlock on error too
        self.unlockItems(newRecordItems)
      })
    }
  }

  getNewRecordItems() {
    return this.itemTargets.filter(item => item.dataset.newRecord === 'true')
  }

  lockItems(items) {
    items.forEach(item => {
      const input = item.querySelector('input[name*="[description]"]')
      if (input) {
        input.readOnly = true
        input.classList.add('bg-gray-100', 'cursor-wait')
      }
    })
  }

  unlockItems(items) {
    items.forEach(item => {
      const input = item.querySelector('input[name*="[description]"]')
      if (input) {
        input.readOnly = false
        input.classList.remove('bg-gray-100', 'cursor-wait')
      }
    })
  }

  syncItemIds(savedContexts) {
    this.itemTargets.forEach(item => {
      const isNewRecord = item.dataset.newRecord === 'true'
      if (!isNewRecord) return

      const descriptionInput = item.querySelector('input[name*="[description]"]')
      const idInput = item.querySelector('input[name*="[id]"]')

      if (!descriptionInput || !idInput) return

      const currentDescription = descriptionInput.value

      // Find matching saved context by description
      const savedContext = savedContexts.find(ctx =>
        ctx.description === currentDescription && ctx.id
      )

      if (savedContext) {
        // Update the hidden id field
        idInput.value = savedContext.id

        // Update input names from temp index to real id
        this.updateInputNames(item, savedContext.id)

        // Mark as no longer a new record
        item.dataset.newRecord = 'false'
      }
    })
  }

  updateInputNames(item, id) {
    const tempIndex = item.dataset.tempIndex
    if (!tempIndex || tempIndex === 'NEW_ESCALATION_CONTEXT') return

    // Update all input names to use the real database id
    item.querySelectorAll('input, select, textarea').forEach(input => {
      if (input.name) {
        input.name = input.name.replace(
          `escalation_contexts_attributes][${tempIndex}]`,
          `escalation_contexts_attributes][${id}]`
        )
      }
    })
  }
}
